import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
// Api
import { GET_STREAM_VOTE_RESULTS } from 'api/matchup/queries';
import { VOTE_MATCHUP } from 'api/matchup/mutations';
import { GET_TICKET_EVENT_VOTE_RESULTS } from 'api/ticketEvents/queries';
// Types
import { MatchupCorner } from 'api/graphql-global-types';
import {
  VoteMatchup,
  VoteMatchupVariables,
} from 'api/matchup/types/VoteMatchup';
// Helpers
import { getProperErrorMessage } from 'helpers/errors';
import { getEnvLink } from 'helpers/routes';
// Components
import { showToast } from 'components/common/Toast/Toast';
// Hooks
import { useAppContext } from 'hooks';
import FightCard from './FightCard/FightCard';
import SharePickModal from './SharePickModal/SharePickModal';
// Helpers
import {
  Matchup,
  MatchupResults,
  getCompetitorDisplayName,
  getBoutNumbers,
  getCornerCompetitor,
  sortMatchups,
  upsertMatchupResults,
} from 'helpers/streamMatchups';
// Styles
import styles from './StreamMatchups.module.scss';

type StreamMatchupsProps = {
  streamId: string;
  /** Set for the card of an event without a stream, whose votes are kept per event. */
  ticketEventId?: string;
  matchups: Matchup[];
  fanPollingEnabled: boolean;
  sharePickGraphicsEnabled: boolean;
  isVotingOpen: boolean;
  streamName: string;
  streamUrl: string;
  streamStartDate: string;
};

/** Either vote-results query, keyed by its root field. */
type VoteResultsData = Partial<Record<string, MatchupResults[]>>;

const buildStructuredData = (
  matchups: Matchup[],
  streamName: string,
  streamUrl: string,
  streamStartDate: string
) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: `${streamName} fight card`,
  numberOfItems: matchups.length,
  itemListElement: matchups.map((matchup, index) => {
    const competitors = [
      getCornerCompetitor(matchup, MatchupCorner.A),
      getCornerCompetitor(matchup, MatchupCorner.B),
    ];
    const names = competitors.map(getCompetitorDisplayName);

    return {
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'SportsEvent',
        '@id': `${streamUrl}#bout-${matchup.id}`,
        superEvent: { '@type': 'SportsEvent', '@id': streamUrl },
        name: names.join(' vs '),
        startDate: streamStartDate,
        ...(matchup.category ? { sport: matchup.category } : {}),
        competitor: competitors.map((competitor, competitorIndex) => ({
          '@type': 'Person',
          name: names[competitorIndex],
          ...(competitor?.store?.slug
            ? { url: `${getEnvLink()}/${competitor.store.slug}` }
            : {}),
        })),
      },
    };
  }),
});

const StreamMatchups = ({
  streamId,
  ticketEventId,
  matchups,
  fanPollingEnabled,
  sharePickGraphicsEnabled,
  isVotingOpen,
  streamName,
  streamUrl,
  streamStartDate,
}: StreamMatchupsProps): JSX.Element | null => {
  const { token, setLoginModalVisibility } = useAppContext();
  const [sharedMatchupId, setSharedMatchupId] = useState<string | null>(null);
  const [votingMatchupId, setVotingMatchupId] = useState<string | null>(null);
  const [castVotes, setCastVotes] = useState<Record<string, MatchupResults>>(
    {}
  );

  const sortedMatchups = useMemo(() => sortMatchups(matchups), [matchups]);
  const boutNumbers = useMemo(() => getBoutNumbers(sortedMatchups), [
    sortedMatchups,
  ]);

  // A card without a stream keeps its votes on the ticket event.
  const voteResultsSource = ticketEventId
    ? {
        query: GET_TICKET_EVENT_VOTE_RESULTS,
        variables: { ticketEventId },
        field: 'getTicketEventVoteResults',
      }
    : {
        query: GET_STREAM_VOTE_RESULTS,
        variables: { streamId },
        field: 'getStreamVoteResults',
      };

  const { data: voteResultsData } = useQuery<VoteResultsData>(
    voteResultsSource.query,
    {
      variables: voteResultsSource.variables,
      skip: !(ticketEventId || streamId) || matchups.length === 0,
      fetchPolicy: 'cache-and-network',
    }
  );
  const voteResults = voteResultsData?.[voteResultsSource.field];

  const [voteMatchup] = useMutation<VoteMatchup, VoteMatchupVariables>(
    VOTE_MATCHUP
  );

  const resultsByMatchupId = useMemo(() => {
    const map = new Map<string, MatchupResults>();

    (voteResults || []).forEach((result) => {
      map.set(result.matchupId, result);
    });

    Object.values(castVotes).forEach((result) => {
      map.set(result.matchupId, result);
    });

    return map;
  }, [voteResults, castVotes]);

  if (sortedMatchups.length === 0) {
    return null;
  }

  const boutCountLabel =
    sortedMatchups.length === 1 ? '1 BOUT' : `${sortedMatchups.length} BOUTS`;

  const canVote = fanPollingEnabled && isVotingOpen;

  const handleVote = async (matchupId: string, competitorId: string) => {
    if (!token?.accessToken) {
      setLoginModalVisibility(true);

      return;
    }

    setVotingMatchupId(matchupId);

    try {
      const response = await voteMatchup({
        variables: { input: { matchupId, competitorId } },
        update(cache, { data: voteData }) {
          const result = voteData?.voteMatchup;

          if (!result) {
            return;
          }

          const { query, variables, field } = voteResultsSource;
          const existing = cache.readQuery<VoteResultsData>({
            query,
            variables,
          });

          cache.writeQuery<VoteResultsData>({
            query,
            variables,
            data: {
              [field]: upsertMatchupResults(existing?.[field] || [], result),
            },
          });
        },
      });

      const result = response.data?.voteMatchup;

      if (result) {
        setCastVotes((previous) => ({
          ...previous,
          [result.matchupId]: result,
        }));
      }
    } catch (error) {
      showToast({
        message: getProperErrorMessage(
          error,
          'We could not record your vote. Please try again.'
        ),
        type: 'error',
      });
    } finally {
      setVotingMatchupId(null);
    }
  };

  const sharedMatchup =
    sortedMatchups.find((matchup) => matchup.id === sharedMatchupId) || null;

  return (
    <section className={styles.root} aria-labelledby="stream-matchups-heading">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            buildStructuredData(
              sortedMatchups,
              streamName,
              streamUrl,
              streamStartDate
            )
          ).replace(/</g, '\\u003c'),
        }}
      />

      <header className={styles.header}>
        <div className={styles.headerText}>
          <h2 id="stream-matchups-heading" className={styles.title}>
            Matchups
          </h2>
          {canVote && (
            <p className={styles.subtitle}>
              Vote on who you think will win each matchup.
            </p>
          )}
        </div>
        <p className={styles.boutCount}>{boutCountLabel}</p>
      </header>

      <div className={styles.cards}>
        {sortedMatchups.map((matchup, index) => (
          <FightCard
            key={matchup.id}
            matchup={matchup}
            boutNumber={boutNumbers[index]}
            results={resultsByMatchupId.get(matchup.id) || null}
            canVote={canVote}
            canShare={sharePickGraphicsEnabled}
            isVoting={votingMatchupId === matchup.id}
            onVote={(competitorId) => handleVote(matchup.id, competitorId)}
            onSharePick={setSharedMatchupId}
          />
        ))}
      </div>

      {sharedMatchup && (
        <SharePickModal
          open
          matchup={sharedMatchup}
          results={resultsByMatchupId.get(sharedMatchup.id) || null}
          streamName={streamName}
          onClose={() => setSharedMatchupId(null)}
        />
      )}
    </section>
  );
};

export default StreamMatchups;
